/**
 * The set of link-target kinds a Linkview tile can point at (Anke, 2026-09-04:
 * "ein komplexes Objekt unterschiedlicher Klasse ... die serialisierbar sind" —
 * a small class hierarchy instead of several competing optional fields on
 * `LinkTile`). Extending this later (e.g. an `IssueLinkTarget`/`ProjectLinkTarget`)
 * means: a new kind literal here, a new `LinkTarget` subclass, a new tile
 * component, and one entry each in `link-target-serializer.ts`'s discriminator
 * config and `tiles/tile-registry.ts`'s `TILE_COMPONENT_BY_KIND` — nothing else
 * changes.
 */
export type LinkTargetKind = 'external' | 'knowledge-object';

/**
 * Base class for every "what a Linkview tile links to" variant. Deliberately a
 * plain, serializable value-object hierarchy — not a `class-transformer` DTO in
 * the sense `Entity`/`TransformerHelper` use elsewhere in this app (that whole
 * machinery assumes a real backend CommandBus round-trip, see
 * `LocalStorageLinkviewRepository`'s own doc comment). `link-target-serializer.ts`
 * uses `class-transformer`'s lightweight `@Type()`/discriminator support purely
 * as a "which kind → which class" dispatch helper, nothing more.
 *
 * `toJSON()` is picked up by `JSON.stringify` automatically (including nested
 * inside arrays/objects, e.g. a `LinkTile[]` array being persisted) — no special
 * handling needed at the `StorageHelper.set()` call site.
 */
export abstract class LinkTarget {

    public abstract readonly kind: LinkTargetKind;

    public abstract toJSON(): Record<string, unknown>;

}
